import { decimal, percentOf } from "./money.js";
import { manualRange } from "./range.js";
/** The maximum magnitude of a signed distance from its range bound, in percent. */
export const STOP_DISTANCE_MAX = "50";
/** A requested stop distance: "-50" to "50", at most one decimal, no leading zero. */
export function isStopDistance(text) {
    return (/^-?(0|[1-9]\d?)(\.\d)?$/.test(text) &&
        decimal(text).compare(decimal(STOP_DISTANCE_MAX)) <= 0 &&
        decimal(text).compare(decimal(`-${STOP_DISTANCE_MAX}`)) >= 0);
}
/**
 * The distance per canonical bound, `null` for a bound without a stop. A per-side document names
 * both; only a stored legacy document (one `thresholdPercent` plus `side`) can leave a bound
 * unguarded. `enabled` is not consulted: a disabled document still states what was saved.
 */
export function stopDistances(policy) {
    if (policy.lowerThresholdPercent !== undefined || policy.upperThresholdPercent !== undefined) {
        if (policy.lowerThresholdPercent === undefined || policy.upperThresholdPercent === undefined)
            throw new Error("Invalid stop policy");
        if (!isStopDistance(policy.lowerThresholdPercent) ||
            !isStopDistance(policy.upperThresholdPercent))
            throw new Error("Invalid stop policy");
        return { lower: policy.lowerThresholdPercent, upper: policy.upperThresholdPercent };
    }
    const threshold = policy.thresholdPercent;
    // Zero exists only in the per-side form; a legacy document was always saved above zero.
    if (threshold === undefined ||
        !["below", "above", "both"].includes(policy.side ?? "") ||
        decimal(threshold).units <= 0n)
        throw new Error("Invalid stop policy");
    return {
        lower: policy.side === "above" ? null : threshold,
        upper: policy.side === "below" ? null : threshold,
    };
}
export function calculateStops(lowerPrice, upperPrice, policy) {
    manualRange(lowerPrice, upperPrice);
    // Before `stopDistances`, so a disabled document keeps storing no triggers at all.
    if (!policy.enabled)
        return {};
    const distances = stopDistances(policy);
    const threshold = (value) => {
        const result = decimal(value);
        if (result.compare(decimal("-50")) < 0 || result.compare(decimal("100")) >= 0)
            throw new Error("Invalid stop policy");
        return result;
    };
    const lower = distances.lower === null ? null : threshold(distances.lower);
    const upper = distances.upper === null ? null : threshold(distances.upper);
    // Zero is the bound itself, returned verbatim: a division at finite precision could land a hair
    // inside the range and fire before the price actually leaves it.
    const stops = {
        ...(lower !== null
            ? {
                lowerTrigger: lower.units === 0n
                    ? lowerPrice
                    : policy.thresholdInverted
                        ? decimal(lowerPrice).mul(decimal("100")).div(decimal("100").add(lower)).toString()
                        : percentOf(lowerPrice, decimal("100").sub(lower).toString()),
            }
            : {}),
        ...(upper !== null
            ? {
                upperTrigger: upper.units === 0n
                    ? upperPrice
                    : policy.thresholdInverted
                        ? decimal(upperPrice).mul(decimal("100")).div(decimal("100").sub(upper)).toString()
                        : percentOf(upperPrice, decimal("100").add(upper).toString()),
            }
            : {}),
    };
    // Inward stops must survive finite division precision and remain strictly inside both bounds.
    // Ordered triggers alone would admit an inward stop beyond the opposite LP boundary.
    for (const [distance, trigger] of [
        [lower, stops.lowerTrigger],
        [upper, stops.upperTrigger],
    ]) {
        if (distance !== null &&
            distance.units < 0n &&
            (trigger === undefined ||
                decimal(trigger).compare(decimal(lowerPrice)) <= 0 ||
                decimal(trigger).compare(decimal(upperPrice)) >= 0))
            throw new Error("Negative stops must stay strictly inside the LP range.");
    }
    if (stops.lowerTrigger !== undefined &&
        stops.upperTrigger !== undefined &&
        decimal(stops.lowerTrigger).compare(decimal(stops.upperTrigger)) >= 0)
        throw new Error("The lower stop must stay below the upper stop.");
    return stops;
}
export function stopTriggered(price, stops) {
    const p = decimal(price);
    if (p.units <= 0n)
        throw new Error("Invalid price");
    return ((stops.lowerTrigger !== undefined && p.compare(decimal(stops.lowerTrigger)) <= 0) ||
        (stops.upperTrigger !== undefined && p.compare(decimal(stops.upperTrigger)) >= 0));
}
export function rangeMarkers(currentPrice, lowerPrice, upperPrice, policy) {
    const stops = calculateStops(lowerPrice, upperPrice, policy);
    const points = [
        { kind: "min", price: lowerPrice },
        { kind: "max", price: upperPrice },
        { kind: "current", price: currentPrice },
        ...(stops.lowerTrigger ? [{ kind: "lowerStop", price: stops.lowerTrigger }] : []),
        ...(stops.upperTrigger ? [{ kind: "upperStop", price: stops.upperTrigger }] : []),
    ];
    points.sort((a, b) => decimal(a.price).compare(decimal(b.price)));
    const low = decimal(points[0]?.price ?? lowerPrice), span = decimal(points.at(-1)?.price ?? upperPrice).sub(low);
    return points.map((point) => ({
        ...point,
        percent: decimal(point.price).sub(low).mul(decimal("100")).div(span).toString(),
    }));
}
export const CANONICAL_MINTS = {
    SOL: "So11111111111111111111111111111111111111112",
    USDT: "Es9vMFrzaCERmJfrF4H2FYD4KCoNkY11McCe8BenwNYB",
    USDC: "EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v",
};
export function defaultTargetMint(tokenA, tokenB) {
    return Object.values(CANONICAL_MINTS).find((mint) => mint === tokenA || mint === tokenB) ?? null;
}
